import type { RenderIndexInput } from "./types.js";

/**
 * Render an `_INDEX.md` body from a {@link RenderIndexInput}.
 *
 * Output shape:
 *
 *   ---
 *   type: index
 *   status: active
 *   privacy: <privacy>
 *   updated: <updated>
 *   tags: [index, <extraTags...>]
 *   ---
 *
 *   # <title>
 *
 *   > <description>
 *
 *   ## Items (<count>)
 *
 *   | File | Description | Updated |
 *   |---|---|---|
 *   | [[<name>]] | <description or title> | <updated> |
 *   ...
 *
 *   ## Related  (only if `related` is non-empty)
 *
 *   - [[<related[0]>]]
 *   ...
 *
 * Caller is responsible for writing this body to disk. The renderer never
 * touches the filesystem.
 */
export function renderIndex(input: RenderIndexInput): string {
  const updated = input.updated ?? today();
  const privacy = input.privacy ?? "internal";
  const tags = ["index", ...(input.extraTags ?? [])];
  const tagsLine = tags.map((t) => `"${t}"`).join(", ");

  const lines: string[] = [
    "---",
    "type: index",
    "status: active",
    `privacy: ${privacy}`,
    `updated: ${updated}`,
    `tags: [${tagsLine}]`,
    "---",
    "",
    `# ${input.title}`,
    "",
  ];

  if (input.description && input.description.length > 0) {
    lines.push(`> ${input.description}`, "");
  }

  lines.push(`## Items (${input.entries.length})`, "");

  if (input.entries.length === 0) {
    lines.push("(empty)", "");
  } else {
    // Add a scope column only when some entry sets `scope` — so a
    // `_memory` index surfaces which rules are always-on (load every session)
    // while other categories' indexes keep the original three columns.
    const hasScope = input.entries.some((e) => e.scope);
    if (hasScope) {
      lines.push("| File | Description | Scope | Updated |", "|---|---|---|---|");
      for (const e of input.entries) {
        const desc = sanitizeCell(e.description ?? e.title);
        const scope = sanitizeCell(e.scope ?? "");
        const upd = e.updated ?? "—";
        lines.push(`| [[${e.name}]] | ${desc} | ${scope} | ${upd} |`);
      }
    } else {
      lines.push("| File | Description | Updated |", "|---|---|---|");
      for (const e of input.entries) {
        const desc = sanitizeCell(e.description ?? e.title);
        const upd = e.updated ?? "—";
        lines.push(`| [[${e.name}]] | ${desc} | ${upd} |`);
      }
    }
    lines.push("");
  }

  if (input.related && input.related.length > 0) {
    lines.push("## Related", "");
    for (const r of input.related) {
      lines.push(`- [[${r}]]`);
    }
    lines.push("");
  }

  return lines.join("\n");
}

function sanitizeCell(s: string): string {
  // Markdown tables: pipe must be escaped, newlines collapse to spaces.
  return s.replace(/\|/g, "\\|").replace(/\r?\n/g, " ").trim();
}

function today(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
